import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/UI/Modal/ModalPrimitive'
import {
  VariantForm,
  FormVariant,
} from 'components/ProductFlow/ProductModal/VariantForm'
import type { UpdateVariantMutationRequest } from 'services/products'
import { useGetVariantDetail, useUpdateVariant } from 'services/products'
import { usePreferences } from 'context/preferences/PreferencesContext'

export interface EditVariantModalProps {
  show: boolean
  variantId: number
  onHide: () => void
  onSuccess: () => void
}

export function EditVariantModal({
  show,
  variantId,
  onHide,
  onSuccess,
}: EditVariantModalProps) {
  const updateVariantMutation = useUpdateVariant()
  const { preferences, isLoading: isPreferencesLoading } = usePreferences()
  const reportingUnit = preferences?.reporting_unit

  // Fetch variant data for editing
  const { data: variantData, isLoading } = useGetVariantDetail(
    { id: variantId },
    { query: { enabled: show && !!variantId } }
  )

  // `syndigo_product` is present in the backend serializer behind a feature
  // flag; drf-spectacular doesn't see the dynamic field so it's missing
  // from the generated type. Cast to access it.
  const isSyndigoVariant = !!(
    variantData as { syndigo_product?: unknown } | undefined
  )?.syndigo_product

  const handleSave = async (formData: FormVariant): Promise<void> => {
    try {
      await updateVariantMutation.mutateAsync({
        id: variantId,
        data: formData as UpdateVariantMutationRequest,
      })

      // Success notification handled by mutation hook
      onSuccess()
      onHide()
    } catch (err) {
      console.error('Failed to update variant:', err)
      // Error notification handled by mutation hook
      throw err
    }
  }

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onHide()}>
      <DialogContent id="edit-variant-modal-content" size="lg" className="px-3">
        <DialogHeader>
          <DialogTitle>Edit Variant</DialogTitle>
        </DialogHeader>
        <div className="mt-4 overflow-auto">
          {isLoading || isPreferencesLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-gray-500">
                {isPreferencesLoading
                  ? 'Loading...'
                  : 'Loading variant data...'}
              </div>
            </div>
          ) : variantData ? (
            <VariantForm
              onSave={handleSave}
              onCancel={onHide}
              initialData={variantData as unknown as FormVariant}
              reportingUnit={reportingUnit}
              isSyndigoVariant={isSyndigoVariant}
            />
          ) : (
            <div className="text-red-600 p-4">Failed to load variant data</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
